import { useCallback, useEffect, type RefObject } from 'react';

const LEFT_ARROW_KEY_CODE = 'ArrowLeft';
const RIGHT_ARROW_KEY_CODE = 'ArrowRight';
const ENTER_KEY_CODE = 'Enter';
const NUMPAD_ENTER_KEY_CODE = 'NumpadEnter';
const ESCAPE_KEY_CODE = 'Escape';
const TAB_KEY_CODE = 'Tab';

/**
 * This component is intended to make a separation between
 * how the input events are handled and what the consumer
 * expects or cares about.
 *
 * Right now handles only keyboard events, but this could be
 * extended to any kind of input.
 */

interface ExploreModeA11yControlsProps {
  /** Any ref to which a keydown event handler will attach to */
  ref: RefObject<Element>;
  /** Callback when a Left event happened
   * @param largeStep - should move faster
   * @param repeat - the movement is being repeated more than once
   *                 (useful for performance considerations)
   */
  onLeft?: (largerStep: boolean, repeat: boolean) => void;
  /** Callback when a Right event happened */
  onRight?: (largerStep: boolean, repeat: boolean) => void;
  /** Callback when an Accept event happened */
  onAccept?: () => void;
  /** Callback when a Cancel event happened */
  onCancel?: () => void;
  /** Callback when a Tab event happened
   * Allow consumers to prevent default behavior for it.
   */
  onTab?: (preventDefault: () => void, isPrev: boolean) => void;
}

export const useKeyboardControls = ({
  ref,
  onLeft,
  onRight,
  onAccept,
  onCancel,
  onTab,
}: ExploreModeA11yControlsProps) => {
  const keydownHandler: EventListenerOrEventListenerObject = useCallback(
    (e: Event) => {
      if (e instanceof KeyboardEvent) {
        const { code, shiftKey, repeat, preventDefault } = e;
        switch (code) {
          case LEFT_ARROW_KEY_CODE:
            onLeft?.(shiftKey, repeat);
            break;
          case RIGHT_ARROW_KEY_CODE:
            onRight?.(shiftKey, repeat);
            break;
          case ENTER_KEY_CODE:
          case NUMPAD_ENTER_KEY_CODE:
            onAccept?.();
            break;
          case ESCAPE_KEY_CODE:
            onCancel?.();
            break;
          case TAB_KEY_CODE:
            onTab?.(preventDefault.bind(e), shiftKey);
            break;
        }
      }
    },
    [onLeft, onRight, onAccept, onCancel, onTab],
  );

  useEffect(() => {
    const refCurrent = ref.current;
    refCurrent?.addEventListener(
      'keydown' as keyof ElementEventMap,
      keydownHandler,
    );

    return () => {
      refCurrent?.removeEventListener(
        'keydown' as keyof ElementEventMap,
        keydownHandler,
      );
    };
  }, [keydownHandler, ref]);
};
